import { normalizeLocale, type SupportedLocale } from './config';
import {
  getImageInpaintCopy,
  type ImageInpaintCopy,
} from './image-inpaint-copy';

export const IMAGE_REGION_EDIT_COPY_KEYS = [
  'region',
  'motif',
  'ratio',
  'tooSmall',
  'sourceChanged',
  'before',
  'after',
  'compare',
  'apply',
  'applying',
  'applied',
  'appliedBadge',
  'defaultVersion',
  'openEdited',
  'restore',
  'restored',
  'sourceUnavailable',
  'changed',
  'failed',
  'autoRegion',
  'noTarget',
  'ambiguousTarget',
  'chooseArea',
] as const;

export type ImageRegionEditCopy = Record<
  (typeof IMAGE_REGION_EDIT_COPY_KEYS)[number],
  string
> &
  ImageInpaintCopy;

// Column order follows IMAGE_REGION_EDIT_COPY_KEYS; the locale test checks completeness.
export const IMAGE_REGION_EDIT_TRANSLATIONS: Record<SupportedLocale, string[]> =
  {
    en: [
      'Inpainting',
      'Copy from area',
      'Region ratio',
      'This image is too small for a region edit.',
      'This image changed after it was opened. Open it again to select a region.',
      'Before',
      'After',
      'Compare with original',
      'Apply',
      'Applying…',
      'Applied. You can restore the original at any time.',
      'Applied',
      'An edited version is the default for this image.',
      'Open edited version',
      'Restore original',
      'Original restored.',
      'The original image is not available to you.',
      'The original changed after this edit was made.',
      'Could not update the image. Try again.',
      'The edit area follows your marks. Drag its frame to adjust it.',
      'No mark points at an area to change. Select a mark on the area, or draw a Region.',
      'Your marks are spread across the image. Select one mark, or draw a Region.',
      'Choose the area to edit. Select a mark, or draw a Region.',
    ],
    ar: [
      'الرسم الداخلي',
      'النسخ من منطقة',
      'نسبة المنطقة',
      'هذه الصورة صغيرة جدًا لتعديل منطقة.',
      'تغيرت هذه الصورة بعد فتحها. افتحها مرة أخرى لتحديد منطقة.',
      'قبل',
      'بعد',
      'المقارنة مع الأصل',
      'تطبيق',
      'جارٍ التطبيق…',
      'تم التطبيق. يمكنك استعادة الأصل في أي وقت.',
      'مطبّق',
      'نسخة معدّلة هي الافتراضية لهذه الصورة.',
      'فتح النسخة المعدّلة',
      'استعادة الأصل',
      'تمت استعادة الأصل.',
      'الصورة الأصلية غير متاحة لك.',
      'تغيّر الأصل بعد إجراء هذا التعديل.',
      'تعذر تحديث الصورة. حاول مرة أخرى.',
      'تتبع منطقة التعديل علاماتك. اسحب إطارها لضبطها.',
      'لا توجد علامة تشير إلى منطقة للتغيير. حدد علامة على المنطقة، أو ارسم منطقة.',
      'علاماتك منتشرة في أنحاء الصورة. حدد علامة واحدة، أو ارسم منطقة.',
      'اختر المنطقة المراد تعديلها. حدد علامة، أو ارسم منطقة.',
    ],
    es: [
      'Inpainting',
      'Copiar desde un área',
      'Proporción de la región',
      'Esta imagen es demasiado pequeña para editar una región.',
      'La imagen cambió después de abrirla. Ábrela de nuevo para seleccionar una región.',
      'Antes',
      'Después',
      'Comparar con el original',
      'Aplicar',
      'Aplicando…',
      'Aplicado. Puedes restaurar el original en cualquier momento.',
      'Aplicado',
      'Una versión editada es la predeterminada para esta imagen.',
      'Abrir versión editada',
      'Restaurar original',
      'Original restaurado.',
      'La imagen original no está disponible para ti.',
      'El original cambió después de hacer esta edición.',
      'No se pudo actualizar la imagen. Inténtalo de nuevo.',
      'El área de edición sigue tus marcas. Arrastra su marco para ajustarla.',
      'Ninguna marca señala un área para cambiar. Selecciona una marca en el área o dibuja una región.',
      'Tus marcas están repartidas por la imagen. Selecciona una marca o dibuja una región.',
      'Elige el área que quieres editar. Selecciona una marca o dibuja una región.',
    ],
    'pt-BR': [
      'Inpainting',
      'Copiar de uma área',
      'Proporção da região',
      'Esta imagem é pequena demais para editar uma região.',
      'A imagem mudou depois de aberta. Abra-a novamente para selecionar uma região.',
      'Antes',
      'Depois',
      'Comparar com o original',
      'Aplicar',
      'Aplicando…',
      'Aplicado. Você pode restaurar o original a qualquer momento.',
      'Aplicado',
      'Uma versão editada é a padrão para esta imagem.',
      'Abrir versão editada',
      'Restaurar original',
      'Original restaurado.',
      'A imagem original não está disponível para você.',
      'O original mudou depois que esta edição foi feita.',
      'Não foi possível atualizar a imagem. Tente novamente.',
      'A área de edição acompanha suas marcações. Arraste o quadro para ajustá-la.',
      'Nenhuma marcação aponta para uma área a mudar. Selecione uma marcação na área ou desenhe uma região.',
      'Suas marcações estão espalhadas pela imagem. Selecione uma marcação ou desenhe uma região.',
      'Escolha a área a editar. Selecione uma marcação ou desenhe uma região.',
    ],
    fr: [
      'Inpainting',
      'Copier depuis une zone',
      'Format de la zone',
      'Cette image est trop petite pour modifier une zone.',
      'L’image a changé après son ouverture. Rouvrez-la pour sélectionner une zone.',
      'Avant',
      'Après',
      'Comparer avec l’original',
      'Appliquer',
      'Application…',
      'Appliqué. Vous pouvez restaurer l’original à tout moment.',
      'Appliqué',
      'Une version modifiée est la version par défaut de cette image.',
      'Ouvrir la version modifiée',
      'Restaurer l’original',
      'Original restauré.',
      'L’image d’origine ne vous est pas accessible.',
      'L’original a changé après cette modification.',
      'Impossible de mettre à jour l’image. Réessayez.',
      'La zone de modification suit vos annotations. Faites glisser son cadre pour l’ajuster.',
      'Aucune annotation ne désigne de zone à modifier. Sélectionnez une annotation sur la zone ou dessinez une zone.',
      'Vos annotations sont réparties sur toute l’image. Sélectionnez-en une ou dessinez une zone.',
      'Choisissez la zone à modifier. Sélectionnez une annotation ou dessinez une zone.',
    ],
    it: [
      'Inpainting',
      'Copia da un’area',
      'Proporzioni dell’area',
      'Questa immagine è troppo piccola per modificare un’area.',
      'L’immagine è cambiata dopo l’apertura. Riaprila per selezionare un’area.',
      'Prima',
      'Dopo',
      'Confronta con l’originale',
      'Applica',
      'Applicazione…',
      'Applicato. Puoi ripristinare l’originale in qualsiasi momento.',
      'Applicato',
      'Una versione modificata è quella predefinita per questa immagine.',
      'Apri versione modificata',
      'Ripristina originale',
      'Originale ripristinato.',
      'L’immagine originale non è disponibile per te.',
      'L’originale è cambiato dopo questa modifica.',
      'Impossibile aggiornare l’immagine. Riprova.',
      'L’area di modifica segue i tuoi segni. Trascina la cornice per regolarla.',
      'Nessun segno indica un’area da cambiare. Seleziona un segno sull’area o disegna un’area.',
      'I tuoi segni sono sparsi sull’immagine. Seleziona un segno o disegna un’area.',
      'Scegli l’area da modificare. Seleziona un segno o disegna un’area.',
    ],
    ko: [
      '인페인팅',
      '복사할 영역',
      '영역 비율',
      '이 이미지는 영역을 편집하기에 너무 작습니다.',
      '이미지를 연 후 변경되었습니다. 다시 열어 영역을 선택하세요.',
      '이전',
      '이후',
      '원본과 비교',
      '적용',
      '적용 중…',
      '적용되었습니다. 언제든 원본으로 복원할 수 있습니다.',
      '적용됨',
      '이 이미지의 기본값은 편집된 버전입니다.',
      '편집된 버전 열기',
      '원본 복원',
      '원본이 복원되었습니다.',
      '원본 이미지에 접근할 수 없습니다.',
      '이 편집 후 원본이 변경되었습니다.',
      '이미지를 업데이트하지 못했습니다. 다시 시도하세요.',
      '편집 영역이 표시를 따라갑니다. 프레임을 끌어 조정하세요.',
      '변경할 영역을 가리키는 표시가 없습니다. 영역 위의 표시를 선택하거나 영역을 그리세요.',
      '표시가 이미지 곳곳에 흩어져 있습니다. 표시 하나를 선택하거나 영역을 그리세요.',
      '편집할 영역을 선택하세요. 표시를 선택하거나 영역을 그리세요.',
    ],
    de: [
      'Inpainting',
      'Von Bereich kopieren',
      'Seitenverhältnis des Bereichs',
      'Dieses Bild ist zu klein, um einen Bereich zu bearbeiten.',
      'Das Bild hat sich nach dem Öffnen geändert. Öffne es erneut, um einen Bereich auszuwählen.',
      'Vorher',
      'Nachher',
      'Mit Original vergleichen',
      'Anwenden',
      'Wird angewendet…',
      'Angewendet. Du kannst das Original jederzeit wiederherstellen.',
      'Angewendet',
      'Eine bearbeitete Version ist die Standardversion dieses Bildes.',
      'Bearbeitete Version öffnen',
      'Original wiederherstellen',
      'Original wiederhergestellt.',
      'Das Originalbild ist für dich nicht verfügbar.',
      'Das Original wurde nach dieser Bearbeitung geändert.',
      'Das Bild konnte nicht aktualisiert werden. Versuche es erneut.',
      'Der Bearbeitungsbereich folgt deinen Markierungen. Ziehe seinen Rahmen, um ihn anzupassen.',
      'Keine Markierung zeigt auf einen zu ändernden Bereich. Wähle eine Markierung im Bereich aus oder zeichne einen Bereich.',
      'Deine Markierungen sind über das Bild verteilt. Wähle eine Markierung aus oder zeichne einen Bereich.',
      'Wähle den zu bearbeitenden Bereich. Wähle eine Markierung aus oder zeichne einen Bereich.',
    ],
    ja: [
      'インペインティング',
      'コピー元の範囲',
      '範囲の比率',
      'この画像は範囲の編集には小さすぎます。',
      '開いた後に画像が変更されました。もう一度開いて範囲を選択してください。',
      '変更前',
      '変更後',
      '元の画像と比較',
      '適用',
      '適用中…',
      '適用しました。いつでも元の画像に戻せます。',
      '適用済み',
      'この画像の既定は編集済みバージョンです。',
      '編集済みバージョンを開く',
      '元の画像に戻す',
      '元の画像に戻しました。',
      '元の画像にアクセスできません。',
      'この編集の後で元の画像が変更されました。',
      '画像を更新できませんでした。もう一度お試しください。',
      '編集範囲はマークに合わせて決まります。枠をドラッグして調整できます。',
      '変更する範囲を示すマークがありません。範囲上のマークを選択するか、範囲を描いてください。',
      'マークが画像全体に散らばっています。マークを1つ選択するか、範囲を描いてください。',
      '編集する範囲を選んでください。マークを選択するか、範囲を描いてください。',
    ],
    hi: [
      'इनपेंटिंग',
      'क्षेत्र से कॉपी करें',
      'क्षेत्र का अनुपात',
      'यह छवि क्षेत्र संपादन के लिए बहुत छोटी है।',
      'खोलने के बाद छवि बदल गई। क्षेत्र चुनने के लिए इसे फिर से खोलें।',
      'पहले',
      'बाद में',
      'मूल से तुलना करें',
      'लागू करें',
      'लागू किया जा रहा है…',
      'लागू हो गया। आप कभी भी मूल को बहाल कर सकते हैं।',
      'लागू',
      'इस छवि का डिफ़ॉल्ट एक संपादित संस्करण है।',
      'संपादित संस्करण खोलें',
      'मूल बहाल करें',
      'मूल बहाल हो गया।',
      'मूल छवि आपके लिए उपलब्ध नहीं है।',
      'यह संपादन करने के बाद मूल बदल गया।',
      'छवि अपडेट नहीं हो सकी। फिर से कोशिश करें।',
      'संपादन क्षेत्र आपके निशानों के अनुसार है। समायोजित करने के लिए उसका फ़्रेम खींचें।',
      'कोई निशान बदलने वाले क्षेत्र की ओर संकेत नहीं करता। क्षेत्र पर कोई निशान चुनें, या क्षेत्र बनाएं।',
      'आपके निशान पूरी छवि में फैले हैं। एक निशान चुनें, या क्षेत्र बनाएं।',
      'संपादित करने का क्षेत्र चुनें। कोई निशान चुनें, या क्षेत्र बनाएं।',
    ],
    ru: [
      'Инпейнтинг',
      'Копировать из области',
      'Пропорции области',
      'Это изображение слишком маленькое для правки области.',
      'Изображение изменилось после открытия. Откройте его снова, чтобы выбрать область.',
      'До',
      'После',
      'Сравнить с оригиналом',
      'Применить',
      'Применение…',
      'Применено. Оригинал можно восстановить в любое время.',
      'Применено',
      'Для этого изображения по умолчанию используется изменённая версия.',
      'Открыть изменённую версию',
      'Восстановить оригинал',
      'Оригинал восстановлен.',
      'Исходное изображение вам недоступно.',
      'Оригинал изменился после этой правки.',
      'Не удалось обновить изображение. Попробуйте ещё раз.',
      'Область правки следует за вашими пометками. Перетащите её рамку, чтобы настроить.',
      'Ни одна пометка не указывает на область для изменения. Выберите пометку на области или нарисуйте область.',
      'Пометки разбросаны по всему изображению. Выберите одну пометку или нарисуйте область.',
      'Выберите область для правки. Выберите пометку или нарисуйте область.',
    ],
    zh: [
      '局部重绘',
      '从区域复制',
      '区域比例',
      '此图片太小，无法编辑区域。',
      '图片在打开后已更改。请重新打开以选择区域。',
      '修改前',
      '修改后',
      '与原图比较',
      '应用',
      '正在应用…',
      '已应用。你可以随时恢复原图。',
      '已应用',
      '此图片的默认版本是编辑后的版本。',
      '打开编辑后的版本',
      '恢复原图',
      '已恢复原图。',
      '你无法访问原图。',
      '此编辑完成后原图已更改。',
      '无法更新图片，请重试。',
      '编辑区域会跟随你的标记。拖动框架即可调整。',
      '没有标记指向要修改的区域。请选择该区域上的标记，或绘制区域。',
      '你的标记分散在整张图片上。请选择一个标记，或绘制区域。',
      '请选择要编辑的区域。选择一个标记，或绘制区域。',
    ],
    'zh-TW': [
      '局部重繪',
      '從區域複製',
      '區域比例',
      '此圖片太小，無法編輯區域。',
      '圖片在開啟後已變更。請重新開啟以選取區域。',
      '修改前',
      '修改後',
      '與原圖比較',
      '套用',
      '正在套用…',
      '已套用。你可以隨時還原原圖。',
      '已套用',
      '此圖片的預設版本是編輯後的版本。',
      '開啟編輯後的版本',
      '還原原圖',
      '已還原原圖。',
      '你無法存取原圖。',
      '此編輯完成後原圖已變更。',
      '無法更新圖片，請再試一次。',
      '編輯區域會跟隨你的標記。拖曳框架即可調整。',
      '沒有標記指向要修改的區域。請選取該區域上的標記，或繪製區域。',
      '你的標記分散在整張圖片上。請選取一個標記，或繪製區域。',
      '請選擇要編輯的區域。選取一個標記，或繪製區域。',
    ],
    vi: [
      'Inpainting',
      'Sao chép từ vùng',
      'Tỉ lệ vùng',
      'Ảnh này quá nhỏ để chỉnh sửa một vùng.',
      'Ảnh đã thay đổi sau khi mở. Hãy mở lại để chọn vùng.',
      'Trước',
      'Sau',
      'So sánh với bản gốc',
      'Áp dụng',
      'Đang áp dụng…',
      'Đã áp dụng. Bạn có thể khôi phục bản gốc bất cứ lúc nào.',
      'Đã áp dụng',
      'Phiên bản đã chỉnh sửa là mặc định cho ảnh này.',
      'Mở phiên bản đã chỉnh sửa',
      'Khôi phục bản gốc',
      'Đã khôi phục bản gốc.',
      'Bạn không có quyền truy cập ảnh gốc.',
      'Bản gốc đã thay đổi sau khi chỉnh sửa này được tạo.',
      'Không thể cập nhật ảnh. Hãy thử lại.',
      'Vùng chỉnh sửa đi theo các dấu của bạn. Kéo khung để điều chỉnh.',
      'Không có dấu nào chỉ vào vùng cần thay đổi. Hãy chọn một dấu trên vùng đó hoặc vẽ một vùng.',
      'Các dấu của bạn nằm rải rác khắp ảnh. Hãy chọn một dấu hoặc vẽ một vùng.',
      'Hãy chọn vùng cần chỉnh sửa. Chọn một dấu hoặc vẽ một vùng.',
    ],
    id: [
      'Inpainting',
      'Salin dari area',
      'Rasio area',
      'Gambar ini terlalu kecil untuk mengedit area.',
      'Gambar berubah setelah dibuka. Buka lagi untuk memilih area.',
      'Sebelum',
      'Sesudah',
      'Bandingkan dengan asli',
      'Terapkan',
      'Menerapkan…',
      'Diterapkan. Anda dapat memulihkan gambar asli kapan saja.',
      'Diterapkan',
      'Versi yang diedit menjadi default untuk gambar ini.',
      'Buka versi yang diedit',
      'Pulihkan asli',
      'Gambar asli dipulihkan.',
      'Gambar asli tidak tersedia untuk Anda.',
      'Gambar asli berubah setelah edit ini dibuat.',
      'Tidak dapat memperbarui gambar. Coba lagi.',
      'Area edit mengikuti tanda Anda. Seret bingkainya untuk menyesuaikan.',
      'Tidak ada tanda yang menunjuk area yang akan diubah. Pilih tanda di area itu, atau gambar area.',
      'Tanda Anda tersebar di seluruh gambar. Pilih satu tanda, atau gambar area.',
      'Pilih area yang akan diedit. Pilih tanda, atau gambar area.',
    ],
  };

export function getImageRegionEditCopy(locale: string): ImageRegionEditCopy {
  const values = IMAGE_REGION_EDIT_TRANSLATIONS[normalizeLocale(locale)];
  return {
    ...(Object.fromEntries(
      IMAGE_REGION_EDIT_COPY_KEYS.map((key, index) => [key, values[index]!])
    ) as Omit<ImageRegionEditCopy, keyof ImageInpaintCopy>),
    ...getImageInpaintCopy(locale),
  };
}
